import React, { JSX, useState } from 'react'
import { ShieldExclamationIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { graphqlRequest } from 'lib/helpers'
import { announceStreamKeyReset, RESET_STREAM_KEY_CONFIRM, rotateStreamKey } from 'lib/streamKey'
import { StreamKeyAlertResult } from 'types/api'
import dismissStreamKeyAlertMutation from 'graphql/dismissStreamKeyAlert.graphql'

export const STREAM_KEY_ALERT_COPY =
  'Someone left this stream or was removed. They may still have this stream\'s key. Reset the stream key?'
export const STREAM_KEY_RESET_LABEL = 'Reset stream key'
export const STREAM_KEY_KEEP_LABEL = 'Keep the current key'

export const streamKeyAlertShown = (canManage: boolean, streamKeyAlertAt: string | null | undefined): boolean =>
  canManage && streamKeyAlertAt != null && streamKeyAlertAt !== ''

export const streamKeyAlertVisible = (
  canManage: boolean,
  streamKeyAlertAt: string | null | undefined,
  clearedAt: string | null
): boolean =>
  streamKeyAlertShown(canManage, streamKeyAlertAt) && streamKeyAlertAt !== clearedAt

export const keepCurrentStreamKey = async ({
  eventId,
  seenAt,
  token,
  refetchEvent
}: {
  eventId: string
  seenAt: string
  token: string
  refetchEvent: () => Promise<void>
}): Promise<StreamKeyAlertResult> => {
  const { result } = await graphqlRequest<{ result: StreamKeyAlertResult }>(
    dismissStreamKeyAlertMutation, { eventId, seenAt }, token
  )
  await refetchEvent().catch(() => {})
  return result
}

export const resetStreamKeyFromAlert = async ({
  eventId,
  token,
  refetchEvent,
  confirm = (message: string) => globalThis.confirm(message)
}: {
  eventId: string
  token: string
  refetchEvent: () => Promise<void>
  confirm?: (message: string) => boolean
}): Promise<boolean> => {
  if (!confirm(RESET_STREAM_KEY_CONFIRM)) return false
  await rotateStreamKey(eventId, token)
  announceStreamKeyReset(eventId)
  await refetchEvent().catch(() => {})
  return true
}

export const StreamKeyAlertBanner = ({
  eventId,
  streamKeyAlertAt,
  canManage,
  auth0Token,
  refetchEvent
}: {
  eventId: string
  streamKeyAlertAt: string | null | undefined
  canManage: boolean
  auth0Token: string | undefined
  refetchEvent: () => Promise<void>
}): JSX.Element | null => {
  const [busy, setBusy] = useState<'reset' | 'keep' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [clearedAt, setClearedAt] = useState<string | null>(null)
  if (!streamKeyAlertVisible(canManage, streamKeyAlertAt, clearedAt) || streamKeyAlertAt == null) return null
  const seenAt = streamKeyAlertAt

  const run = async (kind: 'reset' | 'keep'): Promise<void> => {
    if (auth0Token == null || busy != null) return
    setBusy(kind)
    setError(null)
    try {
      if (kind === 'reset') {
        const reset = await resetStreamKeyFromAlert({ eventId, token: auth0Token, refetchEvent })
        if (reset) setClearedAt(seenAt)
      } else {
        const result = await keepCurrentStreamKey({ eventId, seenAt, token: auth0Token, refetchEvent })
        if (result.dismissed) setClearedAt(seenAt)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div
      role='status'
      data-testid='stream-key-alert'
      className='mx-4 mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200 sm:mx-8'
    >
      <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
        <p className='flex min-w-0 items-start gap-2'>
          <ShieldExclamationIcon className='mt-0.5 size-4 shrink-0 text-amber-300' aria-hidden='true' />
          <span>{STREAM_KEY_ALERT_COPY}</span>
        </p>
        <div className='flex shrink-0 flex-wrap items-center gap-2'>
          <button
            type='button'
            onClick={() => { void run('reset') }}
            disabled={busy != null}
            data-testid='stream-key-alert-reset'
            className='inline-flex items-center gap-1.5 rounded-md border border-red-500/40 px-3 py-1.5 text-xs font-medium text-red-300 hover:border-red-500/70 hover:text-red-200 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'reset' && <LoadingSpinner />}
            {STREAM_KEY_RESET_LABEL}
          </button>
          <button
            type='button'
            onClick={() => { void run('keep') }}
            disabled={busy != null}
            data-testid='stream-key-alert-keep'
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy === 'keep' && <LoadingSpinner />}
            {STREAM_KEY_KEEP_LABEL}
          </button>
        </div>
      </div>
      {error != null && (
        <div className='mt-2'>
          <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
        </div>
      )}
    </div>
  )
}
